import React from "react"
import styled from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { Swiper, SwiperSlide } from "swiper/react"
import { A11y, Navigation, Pagination } from "swiper/modules"
import { useMedia } from "react-use"
import "swiper/css"
import "swiper/css/a11y"
import "swiper/css/navigation"
import "swiper/css/pagination"

import { Container, Row, Col } from "../Grid"
import Button from "../Button"
import Link from "../Link"
import ButtonRow from "../FormFields/ButtonRow"
import TestimonialAuthor from "../TestimonialAuthor"
import Snippet from "../Snippet"

import { media, mixins } from "../../styles"
import InstagramSVG from "../../images/icons/instagram.inline.svg"

const arrowOffset = 60

const Wrapper = styled.div`
  background: var(--color-greyLight);
  padding: 50px 20px 60px;

  @media ${media.md} {
    padding: 65px 10px;
  }

  @media ${media.lg} {
    padding: 85px 10px 65px;
  }

  .swiper {
    padding-bottom: 60px;
  }

  .swiper-slide {
    height: auto;
  }

  .swiper-button-disabled {
    display: none;
  }

  .swiper-button-prev,
  .swiper-button-next,
  .swiper-button-disable {
    @media ${media.mdDown} {
      display: none;
    }
  }

  .swiper-pagination {
    @media ${media.md} {
      padding-left: ${arrowOffset}px;
      text-align: left;
    }
  }
`

const Header = styled.div`
  text-align: center;
  margin: 0 0 30px;

  h2 {
    ${mixins.title3}
  }

  @media ${media.md} {
    margin: 0 0 40px;
  }

  @media ${media.lg} {
    margin: 0 0 50px;
  }

  @media ${media.xl} {
    margin: 0 0 80px;
  }
`

const Content = styled.div`
  @media ${media.md} {
    margin: 0 -${arrowOffset}px;
  }
`

const Photo = styled.div`
  margin: 60px auto 30px;
  max-width: 320px;
  position: relative;

  @media ${media.md} {
    max-width: 523px;
    margin: 0 auto 60px;
    padding: 10px ${arrowOffset}px 0 10px;
  }

  @media ${media.lg} {
    padding: 10px ${arrowOffset}px 0;
  }

  @media ${media.xl} {
    padding: 10px 30px 0 0;
  }

  @media ${media.xxl} {
    padding: 10px 0 0;
  }
`

const InstagramLink = styled.a`
  ${mixins.copy4}
  align-items: center;
  display: inline-flex;
  flex-flow: row nowrap;
  line-height: 1;
  margin: 15px 0 0;
  text-decoration: none;
  z-index: 1;
  transition: color var(--transitionTime);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    color: var(--color-spaceblue);
  }

  @media ${media.xl} {
    margin: 0;
    position: absolute;
    top: 72.5%;
    left: 62%;
  }

  @media ${media.xxl} {
    left: 65%;
  }

  svg {
    color: var(--color-spaceblue);
    margin: 0 5px 0 0;
  }
`

const TeamMember = styled.div`
  ${mixins.copy5}
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  outline-offset: 0 !important;
  text-align: center;

  @media ${media.md} {
    align-items: flex-start;
    margin: 0 ${arrowOffset}px;
    text-align: left;
  }
`

const Quote = styled.p`
  ${mixins.copy2}
  margin: 10px 0 0;
`

const Author = styled(TestimonialAuthor)`
  margin: 30px 0 0;

  @media ${media.md} {
    margin: 30px 0;
  }
`

const Team = ({ team }) => {
  const prefersReducedMotion = useMedia(
    "(prefers-reduced-motion: reduce)",
    false
  )

  const data = useStaticQuery(graphql`
    query CareersTeamQuery {
      site {
        siteMetadata {
          socialProfiles {
            includeInFooter
            logoId
            name
            url
            username
          }
        }
      }
      lifeAtSolaris: file(
        relativePath: { eq: "content/careers/life-at-solaris.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 523, height: 424, layout: CONSTRAINED)
        }
      }
    }
  `)

  const sliderParams = {
    modules: [A11y, Navigation, Pagination],
    a11y: {
      enabled: true,
    },
    navigation: true,
    pagination: {
      type: "bullets",
      clickable: true,
    },
    slidesPerView: 1,
    speed: prefersReducedMotion ? 0 : 300,
  }

  const instagramProfile = data.site.siteMetadata.socialProfiles.find(
    (profile) => profile.name === "Instagram"
  )

  return (
    <Container>
      <Wrapper>
        <Header>
          <h2>
            <Snippet id="careersLifeAtSolaris" field="header" />
          </h2>
        </Header>
        <Row>
          <Col md="5" mdOrder="2">
            <Content>
              <Swiper {...sliderParams}>
                {team.map((teamMember, i) => (
                  <SwiperSlide key={i}>
                    <TeamMember tabIndex="0">
                      {teamMember.descriptionShort && (
                        <Quote>{teamMember.descriptionShort}</Quote>
                      )}
                      <Author
                        name={teamMember.nameInformal}
                        photo={teamMember.picture}
                        title={teamMember.title}
                      />
                    </TeamMember>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Content>
          </Col>
          <Col md="6" mdOrder="1">
            <Photo>
              <GatsbyImage
                alt={Snippet({ id: "careersLifeAtSolaris", field: "header" })}
                image={data.lifeAtSolaris.childImageSharp.gatsbyImageData}
              />
              <InstagramLink
                href={instagramProfile.url}
                target="_blank"
                rel="nofollow noreferrer"
              >
                <InstagramSVG aria-hidden="true" />
                {instagramProfile.username}
              </InstagramLink>
            </Photo>
          </Col>
        </Row>
        <ButtonRow>
          <Button
            arrow
            as={Link}
            to={Snippet({ id: "careersLifeAtSolaris", field: "linkUrl" })}
            intl={false}
          >
            {Snippet({ id: "careersLifeAtSolaris", field: "linkText" })}
          </Button>
        </ButtonRow>
      </Wrapper>
    </Container>
  )
}

export default Team
